import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import { useMemo, useState } from "react";
import {
  useCriarTransmissor,
  useRotacionarTransmissor,
  useTransmissores
} from "../../api/ESocialJTServiceApi";
import DataPanel from "../../components/data/DataPanel";
import Page from "../../components/layout/page/Page";
import PageHeader from "../../components/page-header/PageHeader";
import useFeedback from "../../components/feedback/useFeedback";

const TIPOS = [
  { value: "PROCURACAO_ELETRONICA", label: "Procuração" },
  { value: "PROPRIO", label: "Próprio" }
];

function formatarTipoTransmissor(tipo) {
  return TIPOS.find((item) => item.value === tipo)?.label || tipo || "-";
}

function formatarDocumento(documento = "") {
  const digits = String(documento).replace(/\D/g, "");
  if (digits.length === 14) {
    return digits.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      "$1.$2.$3/$4-$5"
    );
  }
  if (digits.length === 11) {
    return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
  }
  return documento || "-";
}

function formatarData(data) {
  if (!data) return "-";
  return new Date(data).toLocaleDateString("pt-BR");
}

function situacaoTransmissor(transmissor) {
  if (!transmissor?.ativo) {
    return { label: "Inativo", color: "default" };
  }
  if (!transmissor?.validoAte) {
    return { label: "Sem validade", color: "warning" };
  }
  const limite = new Date(transmissor.validoAte);
  const hoje = new Date();
  const diff = Math.floor(
    (limite.setHours(0, 0, 0, 0) - hoje.setHours(0, 0, 0, 0)) / 86400000
  );
  if (diff < 0) {
    return { label: "Vencido", color: "error" };
  }
  if (diff <= 30) {
    return {
      label: `Vence em ${diff} dia${diff === 1 ? "" : "s"}`,
      color: "warning"
    };
  }
  return { label: "Válido", color: "success" };
}

function TransmissoresPage() {
  const { showSuccess, showError } = useFeedback();
  const { data = [], isLoading, isFetching, refetch } = useTransmissores();
  const criarTransmissor = useCriarTransmissor();
  const rotacionarTransmissor = useRotacionarTransmissor();

  const [busca, setBusca] = useState("");
  const [dialog, setDialog] = useState(null);
  const [novoForm, setNovoForm] = useState({
    descricao: "",
    documento: "",
    tipo: "PROCURACAO_ELETRONICA",
    senha: ""
  });
  const [rotacaoForm, setRotacaoForm] = useState({
    senha: ""
  });
  const [novoArquivo, setNovoArquivo] = useState(null);
  const [rotacaoArquivo, setRotacaoArquivo] = useState(null);

  const transmissores = useMemo(() => {
    const filtro = busca.trim().toLowerCase();
    if (!filtro) return data;
    return data.filter((item) => {
      const conteudo = [
        item.id,
        item.descricao,
        item.documento,
        item.tipo,
        formatarTipoTransmissor(item.tipo),
        item.titular
      ]
        .join(" ")
        .toLowerCase();
      return conteudo.includes(filtro);
    });
  }, [busca, data]);

  const abrirNovo = () => {
    setNovoForm({
      descricao: "",
      documento: "",
      tipo: "PROCURACAO_ELETRONICA",
      senha: ""
    });
    setNovoArquivo(null);
    setDialog("novo");
  };

  const abrirRotacao = (transmissor) => {
    setDialog({ tipo: "rotacao", transmissor });
    setRotacaoForm({ senha: "" });
    setRotacaoArquivo(null);
  };

  const fecharDialog = () => {
    if (criarTransmissor.isLoading || rotacionarTransmissor.isLoading) {
      return;
    }
    setDialog(null);
  };

  const enviarNovo = async () => {
    if (
      !novoForm.descricao ||
      !novoForm.documento ||
      !novoForm.senha ||
      !novoArquivo
    ) {
      showError("Preencha descrição, documento, senha e arquivo PFX.");
      return;
    }

    const formData = new FormData();
    Object.entries(novoForm).forEach(([key, value]) =>
      formData.append(key, value)
    );
    formData.append("pfx", novoArquivo);

    try {
      await criarTransmissor.mutateAsync(formData);
      showSuccess("Transmissor cadastrado com sucesso.");
      setDialog(null);
      refetch();
    } catch (error) {
      showError(
        error?.response?.data || "Não foi possível cadastrar o transmissor."
      );
    }
  };

  const enviarRotacao = async () => {
    if (!dialog?.transmissor?.id || !rotacaoForm.senha || !rotacaoArquivo) {
      showError("Informe a senha e o novo arquivo PFX.");
      return;
    }

    const formData = new FormData();
    formData.append("senha", rotacaoForm.senha);
    formData.append("pfx", rotacaoArquivo);

    try {
      await rotacionarTransmissor.mutateAsync({
        id: dialog.transmissor.id,
        formData
      });
      showSuccess("Certificado rotacionado no mesmo registro.");
      setDialog(null);
      refetch();
    } catch (error) {
      showError(
        error?.response?.data || "Não foi possível rotacionar o certificado."
      );
    }
  };

  return (
    <Page maxWidth="xl">
      <PageHeader
        primary="Transmissores e certificados"
        secondary="Cadastro, consulta e rotação centralizada do certificado do Gateway eSocial."
        commands={[
          <Button
            key="reload"
            variant="text"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <Icon>refresh</Icon>
          </Button>,
          <Button
            key="new"
            variant="contained"
            startIcon={<Icon>add</Icon>}
            onClick={abrirNovo}
          >
            Novo transmissor
          </Button>
        ]}
      />

      <DataPanel
        loading={isLoading}
        isEmpty={!transmissores.length}
        emptyMessage="Nenhum transmissor cadastrado."
      >
        <Stack spacing={2}>
          <TextField
            label="Buscar transmissor"
            value={busca}
            onChange={(event) => setBusca(event.target.value)}
            fullWidth
          />

          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width="18%">ID</TableCell>
                  <TableCell width="22%">Descrição</TableCell>
                  <TableCell width="14%">Tipo</TableCell>
                  <TableCell width="18%">Documento</TableCell>
                  <TableCell width="16%">Validade</TableCell>
                  <TableCell width="12%">Situação</TableCell>
                  <TableCell align="right" width="10%">
                    Ações
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transmissores.map((item) => {
                  const status = situacaoTransmissor(item);
                  return (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {item.id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.fingerprint || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {item.descricao}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.titular || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {formatarTipoTransmissor(item.tipo)}
                      </TableCell>
                      <TableCell>{formatarDocumento(item.documento)}</TableCell>
                      <TableCell>{formatarData(item.validoAte)}</TableCell>
                      <TableCell>{status.label}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          color="primary"
                          onClick={() => abrirRotacao(item)}
                          disabled={!item.ativo}
                        >
                          <Icon>refresh</Icon>
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        </Stack>
      </DataPanel>

      <Dialog
        open={dialog === "novo"}
        onClose={fecharDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Novo transmissor</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Descrição"
              value={novoForm.descricao}
              onChange={(event) =>
                setNovoForm({ ...novoForm, descricao: event.target.value })
              }
              fullWidth
            />
            <TextField
              label="Documento"
              value={novoForm.documento}
              onChange={(event) =>
                setNovoForm({ ...novoForm, documento: event.target.value })
              }
              fullWidth
            />
            <TextField
              select
              label="Tipo"
              value={novoForm.tipo}
              onChange={(event) =>
                setNovoForm({ ...novoForm, tipo: event.target.value })
              }
              fullWidth
            >
              {TIPOS.map((tipo) => (
                <MenuItem key={tipo.value} value={tipo.value}>
                  {tipo.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Senha do PFX"
              type="password"
              value={novoForm.senha}
              onChange={(event) =>
                setNovoForm({ ...novoForm, senha: event.target.value })
              }
              fullWidth
            />
            <Button variant="outlined" component="label">
              Selecionar PFX
              <input
                hidden
                type="file"
                accept=".pfx,.p12"
                onChange={(event) =>
                  setNovoArquivo(event.target.files?.[0] || null)
                }
              />
            </Button>
            <Typography variant="caption" color="text.secondary">
              {novoArquivo ? novoArquivo.name : "Nenhum arquivo selecionado"}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharDialog}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={enviarNovo}
            disabled={criarTransmissor.isLoading}
          >
            Cadastrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialog?.tipo === "rotacao"}
        onClose={fecharDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Rotacionar certificado</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Transmissor"
              value={dialog?.transmissor?.descricao || ""}
              fullWidth
              disabled
            />
            <TextField
              label="Senha do novo PFX"
              type="password"
              value={rotacaoForm.senha}
              onChange={(event) =>
                setRotacaoForm({ senha: event.target.value })
              }
              fullWidth
            />
            <Button variant="outlined" component="label">
              Selecionar novo PFX
              <input
                hidden
                type="file"
                accept=".pfx,.p12"
                onChange={(event) =>
                  setRotacaoArquivo(event.target.files?.[0] || null)
                }
              />
            </Button>
            <Typography variant="caption" color="text.secondary">
              {rotacaoArquivo
                ? rotacaoArquivo.name
                : "Nenhum arquivo selecionado"}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharDialog}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={enviarRotacao}
            disabled={rotacionarTransmissor.isLoading}
          >
            Rotacionar
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}

export default TransmissoresPage;
